// ============================================================
// TimerLeaveDialog.tsx
// Casevo-styled "you have a timer running" popup, shown when the user
// tries to close a timer modal / navigate away IN-APP with unsaved time.
// Built on the shared DialogueBox shell (surface card, token colours →
// light + dark, scrollable overlay, labelled dialog).
//
// Three choices:
//   • Save  (green primary)  — save the time (entry, or email draft), then leave
//   • Discard (danger text)  — throw the time away, then leave
//   • Keep timing (neutral)  — dismiss; the timer keeps running, stay put
// ============================================================
import React, { useEffect } from 'react'
import DialogueBox from './DialogueBox'
import { BTN_PRIMARY_MD } from '../../styles/buttons'

interface TimerLeaveDialogProps {
  isOpen: boolean
  seconds: number
  running: boolean
  saveLabel: string
  // Optional line under the question (e.g. what "Save" does for email).
  saveHint?: string
  busy?: boolean
  onSave: () => void
  onDiscard: () => void
  onKeep: () => void
}

const formatElapsed = (total: number): string => {
  const s = Math.max(0, Math.floor(total))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${h}h ${m}m ${sec}s` : `${m}m ${sec}s`
}

const TimerLeaveDialog: React.FC<TimerLeaveDialogProps> = ({
  isOpen, seconds, running, saveLabel, saveHint, busy = false, onSave, onDiscard, onKeep,
}) => {
  // Esc = "Keep timing" (the safe choice). Capture phase + stopPropagation so
  // the underlying modal's own Esc handler doesn't also fire.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      if (!busy) onKeep()
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [isOpen, busy, onKeep])

  const elapsed = formatElapsed(seconds)
  return (
    <DialogueBox isOpen={isOpen} title={running ? 'Timer running' : 'Unsaved time'} onClose={() => { if (!busy) onKeep() }}>
      <p className="text-sm text-casevo-forest leading-relaxed">
        {running
          ? <>You have a timer running (<span className="font-semibold tabular-nums">{elapsed}</span>). Save this time, or discard it?</>
          : <>You have unsaved time on this entry (<span className="font-semibold tabular-nums">{elapsed}</span>). Save it, or discard it?</>}
      </p>
      {saveHint && <p className="text-xs text-casevo-text-soft mt-2">{saveHint}</p>}
      <div className="flex flex-col-reverse sm:flex-row gap-2 mt-5">
        <button
          type="button"
          onClick={onKeep}
          disabled={busy}
          className="sm:mr-auto px-4 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-casevo-forest hover:border-casevo-mid disabled:opacity-50 focus:outline-none casevo-focus"
        >
          Keep timing
        </button>
        <button
          type="button"
          onClick={onDiscard}
          disabled={busy}
          className="px-4 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-casevo-danger hover:border-red-300 dark:hover:border-red-400/60 disabled:opacity-50 focus:outline-none casevo-focus"
        >
          Discard
        </button>
        <button type="button" onClick={onSave} disabled={busy} autoFocus className={BTN_PRIMARY_MD}>
          {busy ? 'Saving…' : saveLabel}
        </button>
      </div>
    </DialogueBox>
  )
}

export default TimerLeaveDialog
